import { DEFAULT_TILE_METERS, normalizeHex } from '@/terrain/TerrainSurface';

/**
 * What a ground material *is*, established before any pixels exist.
 *
 * The forge is two calls, not one, and the recipe is why. Asking an image model directly for "a red
 * gravel texture" gets you a plausible red gravel texture — and nothing else. No colour you can put
 * on a swatch chip, no roughness for the shader, no tile size, no answer to whether a plant may be
 * put in it, and no way to tell the render prompt what the ground is made of. Those are the fields
 * the rest of the application actually consumes; the image is only the part you can see.
 *
 * So a text model with web search resolves the request into this first. That call is cheap, it is
 * where the grounding happens — real products have real colours, and "red gravel" is red lava rock
 * at roughly #8f3b2c, not the pure red a model invents from the word — and its `imagePrompt` field
 * is what the image call is then given. When the image call fails or there is no key, the recipe is
 * still a complete material: {@link synthesizeSwatch} can draw one from these numbers alone.
 */
export interface TextureRecipe {
  /** Display name, title case: "Red Lava Rock", "Pennsylvania Bluestone". */
  name: string;
  /** The material's average colour, `#rrggbb`. Drawn on the chip and used where no swatch loads. */
  color: string;
  /** Colours present in the grain, darkest to lightest. Drives the procedural fallback. */
  accents: string[];
  /** 0 (mirror) to 1 (chalk). Water is 0.06; asphalt is 0.55; bark mulch is 0.98. */
  roughness: number;
  /** Whether a plant may be placed on this material. Paving and water are false. */
  plantable: boolean;
  /** Metres one tile of the swatch covers on the ground. */
  tileMeters: number;
  /** Typical size of one unit of the material — a gravel chip, a paver, a bark shred — in metres. */
  grainMeters: number;
  /** What it is, in the language a render prompt wants. Reaches the capture manifest verbatim. */
  character: string;
  /** One line for the ground panel. */
  hint: string;
  /** What the search established. Kept so the numbers above are auditable rather than asserted. */
  grounding: string;
  /** The prompt the image call is given, authored by the model that did the research. */
  imagePrompt: string;
}

export class TextureRecipeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TextureRecipeError';
  }
}

/**
 * Read a recipe from a model reply.
 *
 * Forgiving about values and strict about the two fields that cannot be guessed — a name and a
 * colour. Everything else has a defensible default, and a paid call that came back with fifteen good
 * fields and a missing `grainMeters` should not be thrown away to punish the sixteenth.
 */
export function parseTextureRecipe(raw: unknown): TextureRecipe {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    throw new TextureRecipeError('Expected an object describing the material.');
  }
  const data = raw as Record<string, unknown>;

  const name = text(data.name);
  if (!name) throw new TextureRecipeError('The reply named no material.');

  const color = normalizeHex(data.color);
  if (!color) {
    throw new TextureRecipeError(`No usable colour for “${name}” — expected #rrggbb.`);
  }

  const accents = (Array.isArray(data.accents) ? data.accents : [])
    .map((entry) => normalizeHex(entry))
    .filter((entry): entry is string => entry !== undefined)
    .slice(0, 6);

  const grainMeters = clamp(number(data.grainMeters, 0.03), 0.002, 2);
  // A tile has to hold several units of the material or it reads as a repeating pattern rather than
  // a surface; eight across is the point where the eye stops finding the grid.
  const tileMeters = clamp(number(data.tileMeters, Math.max(DEFAULT_TILE_METERS, grainMeters * 8)), 0.1, 20);

  return {
    name: name.slice(0, 60),
    color,
    accents: accents.length > 0 ? accents : [shade(color, -0.25), shade(color, 0.25)],
    roughness: clamp(number(data.roughness, 0.85), 0, 1),
    plantable: data.plantable !== false,
    tileMeters,
    grainMeters,
    character: text(data.character) ?? `${name.toLowerCase()} ground surface`,
    hint: (text(data.hint) ?? `${name}.`).slice(0, 140),
    grounding: text(data.grounding) ?? '',
    imagePrompt: text(data.imagePrompt) ?? defaultImagePrompt(name, tileMeters),
  };
}

/**
 * A recipe from nothing but a phrase.
 *
 * The path taken when there is no API key: the user still gets a material they can paint with, named
 * what they asked for, in a neutral colour they can correct in the panel. Refusing to make anything
 * without a key would make the whole feature unavailable to someone who just wants a fifth material
 * with a colour of their choosing.
 */
export function fallbackRecipe(request: string): TextureRecipe {
  const name = titleCase(request.trim() || 'New material');
  const color = colorFromWords(request);
  return {
    name: name.slice(0, 60),
    color,
    accents: [shade(color, -0.22), shade(color, 0.22)],
    roughness: 0.85,
    plantable: true,
    tileMeters: DEFAULT_TILE_METERS,
    grainMeters: 0.03,
    character: `${name.toLowerCase()} ground surface`,
    hint: `${name} — made without a grounded search, so check the colour.`,
    grounding: '',
    imagePrompt: defaultImagePrompt(name, DEFAULT_TILE_METERS),
  };
}

/** The instruction wrapper every swatch render gets, whoever wrote the description inside it. */
export function defaultImagePrompt(name: string, tileMeters: number): string {
  return (
    `A seamless, tileable, top-down photographic texture swatch of ${name}, ` +
    `covering exactly ${tileMeters.toFixed(2)} metres square of ground.`
  );
}

/**
 * Colours read straight out of the phrase, for the no-key path.
 *
 * A short table rather than a clever parser: the point is that "red gravel" comes out reddish and
 * "bluestone" comes out blue-grey, which is the difference between a material you can recognise in
 * the palette and a grey rectangle. Anything unmatched lands on a neutral mid-grey, which is honest
 * about knowing nothing.
 */
const COLOR_WORDS: readonly (readonly [RegExp, string])[] = [
  [/\bred|terracotta|brick|lava\b/, '#8f3b2c'],
  [/\bblue|bluestone|slate\b/, '#4a5a68'],
  [/\bgreen|moss|olive\b/, '#5d6b45'],
  [/\bwhite|marble|chalk|quartz\b/, '#ddd8cd'],
  [/\bblack|charcoal|basalt\b/, '#33322f'],
  [/\bbrown|bark|cedar|cocoa\b/, '#5a4230'],
  [/\btan|sand|beige|buff|granite\b/, '#b9a684'],
  [/\bgold|amber|honey\b/, '#a9803f'],
  [/\bpink|rose|salmon\b/, '#b98079'],
  [/\bgrey|gray|silver|concrete\b/, '#8e8d89'],
];

function colorFromWords(request: string): string {
  const lower = request.toLowerCase();
  for (const [pattern, color] of COLOR_WORDS) {
    if (pattern.test(lower)) return color;
  }
  return '#8b8b86';
}

/** Lighten (`amount > 0`) or darken a hex colour. */
export function shade(hex: string, amount: number): string {
  const normalized = normalizeHex(hex) ?? '#888888';
  const channels = [1, 3, 5].map((i) => parseInt(normalized.slice(i, i + 2), 16));
  const shifted = channels.map((value) => {
    const target = amount >= 0 ? 255 : 0;
    return Math.round(value + (target - value) * Math.abs(amount));
  });
  return `#${shifted.map((value) => value.toString(16).padStart(2, '0')).join('')}`;
}

function titleCase(value: string): string {
  return value.replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

function number(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
